import { useMemo } from "react";
import { DxCodeEntryInput } from "../../../../../components/modals/editDxCodeSidePanel/editDxCodeSidePanel";
import {
  NccnIcdCodeEntry,
  NccnIcdConfig,
} from "../../../../../data-model/nccnIcdConfig";
import { NccnRegimenConfig } from "../../../../../data-model/nccnRegimenConfig";
import { DxCodeConfigTab } from "../../../../../../shared/enums/dxCodeConfigTab";

interface EditingConfigData {
  mode: "drug" | "regimen";
  drugName?: string;
  jCode?: string;
  drugType?: string;
  regimenName?: string;
  regimenCode?: string;
  dxCodes: DxCodeEntryInput[];
  hasPriorityConfig: boolean;
}

/**
 * Hook to get editing config data for the currently selected row
 */
/**
 * Creates a priority map from organization-specific priority configuration
 */
const buildPriorityMap = (
  orgPriorityConfig: Record<string, number> | undefined,
): Map<string, number> => {
  const priorityMap = new Map<string, number>();
  if (orgPriorityConfig) {
    Object.entries(orgPriorityConfig).forEach(([dxCode, priority]) => {
      priorityMap.set(dxCode, priority as number);
    });
  }
  return priorityMap;
};

/**
 * Builds and sorts DX codes array with priorities
 */
const buildAndSortDxCodes = (
  entries: NccnIcdCodeEntry[],
  priorityMap: Map<string, number>,
): DxCodeEntryInput[] => {
  const dxCodes: DxCodeEntryInput[] = entries.map((e) => ({
    dxCode: e.code,
    priority: priorityMap.get(e.code),
    source: e.source ?? [],
  }));

  // Sort: codes with priority first (ascending), then codes without priority
  dxCodes.sort((a, b) => {
    // Handle optional priority: codes without priority go to the end
    if (a.priority === undefined && b.priority === undefined) return 0;
    if (a.priority === undefined) return 1;
    if (b.priority === undefined) return -1;
    return a.priority - b.priority;
  });

  return dxCodes;
};

/**
 * Checks if priority configuration exists for an organization
 */
const hasPriorityConfig = (
  orgPriorityConfig: Record<string, number> | undefined,
): boolean => {
  return !!orgPriorityConfig && Object.keys(orgPriorityConfig).length > 0;
};

/**
 * Gets editing config data for drug mode
 */
const getDrugConfigData = (
  editingRowData: any,
  selectedOrgId: string,
  nccnIcdConfigData: NccnIcdConfig[],
): EditingConfigData | null => {
  const config = nccnIcdConfigData.find(
    (c) => c.id === editingRowData.id || c.code === editingRowData.jCode,
  );
  if (!config) return null;

  const orgPriorityConfig =
    config.priority_config_org_specific?.[selectedOrgId];
  const allEntries = (config.icd_codes || []).filter((e) => e.code?.length > 0);
  const priorityMap = buildPriorityMap(orgPriorityConfig);
  const dxCodes = buildAndSortDxCodes(allEntries, priorityMap);

  return {
    mode: "drug" as const,
    drugName: config.category || "",
    jCode: config.code || config.id || "",
    drugType: config.category || "",
    dxCodes,
    hasPriorityConfig: hasPriorityConfig(orgPriorityConfig),
  };
};

/**
 * Gets editing config data for regimen mode
 */
const getRegimenConfigData = (
  editingRowData: any,
  selectedOrgId: string,
  nccnRegimenConfigData: NccnRegimenConfig[],
): EditingConfigData | null => {
  const config = nccnRegimenConfigData.find((c) => c.id === editingRowData.id);
  if (!config) return null;

  const orgPriorityConfig =
    config.priority_config_org_specific?.[selectedOrgId];
  const allEntries = (config.icdcodes || []).filter((e) => e.code?.length > 0);
  const priorityMap = buildPriorityMap(orgPriorityConfig);
  const dxCodes = buildAndSortDxCodes(allEntries, priorityMap);

  return {
    mode: "regimen" as const,
    regimenName: config.regimen_name || "",
    regimenCode: config.regimen_code || "",
    dxCodes,
    hasPriorityConfig: hasPriorityConfig(orgPriorityConfig),
  };
};

export const useEditingConfig = (
  editingRowData: any,
  selectedOrgId: string,
  activeTab: DxCodeConfigTab,
  nccnIcdConfigData: NccnIcdConfig[],
  nccnRegimenConfigData: NccnRegimenConfig[],
): EditingConfigData | null => {
  // Memoize editingConfigData to prevent unnecessary re-renders of the side panel
  const editingConfigData = useMemo(
    (): EditingConfigData | null => {
      if (!editingRowData) return null;

      if (activeTab === DxCodeConfigTab.DRUG) {
        return getDrugConfigData(
          editingRowData,
          selectedOrgId,
          nccnIcdConfigData,
        );
      }

      return getRegimenConfigData(
        editingRowData,
        selectedOrgId,
        nccnRegimenConfigData,
      );
    },
    [
      editingRowData,
      selectedOrgId,
      activeTab,
      nccnIcdConfigData,
      nccnRegimenConfigData,
    ],
  );

  return editingConfigData;
};
